<vs-card class="h-full">
  <vs-card-header
    [config]="{
      title: 'Warranty Overview',
      subtitle: 'Information on this warranty\'s associated customer, policy, and asset.',
    }"
  ></vs-card-header>

  <vs-card-content *ngIf="warranty">
    <vs-tabs theme="primary">
      <vs-tab [config]="{ label: 'Policy' }">
        <ws-warranty-policy-card [warranty]="warranty"></ws-warranty-policy-card>
      </vs-tab>

      <vs-tab [config]="{ label: 'Customer' }">
        <ws-warranty-customer-card
          [customer]="warranty.customer ?? null"
          [warranty]="warranty"
        ></ws-warranty-customer-card>
      </vs-tab>

      <vs-tab [config]="{ label: 'Distributor' }">
        <div class="px-2">
          <ng-container *ngIf="!warranty.distributor">
            <p class="text-sm font-normal text-gray-600 text-justify -mb-1 pr-3">
              This warranty is not linked to a distributor.
              <span *ngIf="canUpdate">Use the picker below to update the linked distributor.</span>
            </p>

            <vs-form-field *ngIf="canUpdate" class="[&>div>div]:!max-w-none" appearance="vertical">
              <ws-distributor-picker
                placeholder="Select a distributor..."
                [onlyActive]="true"
                [includeSubDistributors]="true"
                [value]="warranty.distributor ?? null"
                (valueChanges)="newDistributor = $event"
              ></ws-distributor-picker>

              <button
                *ngIf="(newDistributor?.id ?? null) !== warranty.distributorId"
                vs-button
                size="sm"
                class="mt-2"
                [disabled]="updatingDistributor"
                (click)="updateDistributor()"
              >
                Save changes
              </button>
            </vs-form-field>
          </ng-container>

          <ng-container *ngIf="warranty.distributor">
            <div class="my-4 grid grid-cols-[minmax(84px,auto)_1fr_auto] gap-x-1 gap-y-3">
              <div class="text-sm font-medium text-gray-700">Name</div>
              <div
                tooltipStyle="light"
                tooltipOpenDelay="150"
                class="text-sm truncate"
                [class.col-span-2]="!canUpdate"
                [vsTooltip]="warranty.distributor.name"
              >
                {{ warranty.distributor.name }}
              </div>

              <div *ngIf="canUpdate" class="relative w-16">
                <button
                  vs-button
                  size="sm"
                  theme="danger"
                  appearance="clear"
                  class="absolute left-0 top-1/2 -translate-y-1/2"
                  (click)="confirmationDialog.open()"
                >
                  Unlink
                </button>
              </div>

              <div class="col-span-full border-b border-gray-200"></div>

              <ng-container *ngIf="warranty.distributor.programName as programName">
                <div class="text-sm font-medium text-gray-700">Program</div>
                <div class="text-sm truncate col-span-2">{{ programName }}</div>

                <div class="col-span-full border-b border-gray-200"></div>
              </ng-container>

              <ng-container *ngIf="warranty.distributor.logo as logo">
                <div class="text-sm font-medium text-gray-700">Logo</div>
                <div class="col-span-2">
                  <img [src]="logo" alt="Distributor logo" class="h-8 max-w-[120px] object-contain" />
                </div>

                <div class="col-span-full border-b border-gray-200"></div>
              </ng-container>

              <div class="text-sm font-medium text-gray-700">Email</div>
              <div class="text-sm truncate col-span-2">{{ warranty.distributor.email || '-' }}</div>

              <div class="col-span-full border-b border-gray-200"></div>

              <div class="text-sm font-medium text-gray-700">Phone</div>
              <div class="text-sm truncate col-span-2">{{ warranty.distributor.phone || '-' }}</div>

              <ng-container *ngIf="warranty.distributor.parent">
                <div class="col-span-full border-b border-gray-200"></div>

                <div class="text-sm font-medium text-gray-700">Parent</div>
                <div class="text-sm truncate col-span-2">{{ warranty.distributor.parent.name }}</div>
              </ng-container>
            </div>

            <a
              vs-button
              size="sm"
              theme="info"
              class="-ml-3"
              appearance="clear"
              routerLink="/distributors/{{ warranty.distributorId }}"
            >
              View distributor details
              <vs-icon name="link-04"></vs-icon>
            </a>
          </ng-container>
        </div>
      </vs-tab>

      <vs-tab [config]="{ label: 'Documents' }">
        <!-- Desktop view -->
        <div class="sm:flex hidden gap-4 h-[100%] mt-4">
          <!-- Nav -->
          <div class="flex flex-[2] flex-col gap-4">
            <ng-container *ngFor="let document of documents">
              <div
                *vsLet="selectedDocument?.id === document.id as selected"
                [class]="getNavboxStyle(document.id)"
                [style.width]="selected ? 'calc(100% + 20px)' : '100%'"
                [style.box-shadow]="selected ? '-2px 4px 10px -10px black' : ''"
                (click)="selectDocument(document)"
              >
                <div class="flex justify-between">
                  <p class="font-medium text-lg self-center">{{ document.title }}</p>

                  <div class="flex gap-1" [class.mr-4]="selected">
                    <button
                      *ngIf="document.downloadUrl"
                      vs-icon-button
                      appearance="clear"
                      [disabled]="!document.downloadUrl"
                      (click)="previewDocument(document)"
                    >
                      <vs-icon name="download-01" size="18"></vs-icon>
                    </button>
                  </div>
                </div>

                <vs-divider class="my-2"></vs-divider>

                <p class="font-normal text-sm text-gray-600 mb-2">Created: {{ document.created | date }}</p>
              </div>
            </ng-container>

            <!-- Empty state if no documents -->
            <div *ngIf="documents.length === 0" class="flex flex-col items-center justify-center p-8">
              <vs-icon name="file-x-02" size="48" class="text-gray-400 mb-3"></vs-icon>
              <p class="text-sm text-gray-600 text-center">No documents available for this policy.</p>
            </div>
          </div>

          <!-- Document Viewer -->
          <div
            class="flex bg-base flex-[6] w-full shadow-lg p-3 h-[100%] min-h-[75vh] rounded-b-lg rounded-tr-lg"
            [class.rounded-tl-lg]="selectedDocument?.title !== 'Terms & Conditions'"
          >
            <div class="w-full h-[75vh] rounded-lg">
              <p *ngIf="selectedDocument && !selectedDocument.downloadUrl" class="p-3">
                <i>This document is not available yet.</i>
              </p>
              <object
                *ngIf="selectedDocument?.downloadUrl"
                frameBorder="0"
                scrolling="auto"
                class="w-full h-[75vh] rounded-lg"
                [data]="selectedDocument!.downloadUrl + '#toolbar=0&navpanes=0&scrollbar=0' | safe: 'resourceUrl'"
              ></object>
            </div>
          </div>
        </div>

        <!-- Mobile view -->
        <div class="sm:hidden mt-4">
          <ng-container *ngFor="let document of documents">
            <div class="flex flex-col mb-6">
              <div class="flex justify-between items-center mb-2">
                <p class="text-lg font-semibold text-gray-900">{{ document.title }}</p>
                <button
                  *ngIf="document.downloadUrl"
                  vs-icon-button
                  appearance="clear"
                  [disabled]="!document.downloadUrl"
                  (click)="previewDocument(document)"
                >
                  <vs-icon name="download-01" size="20"></vs-icon>
                </button>
              </div>

              <object
                *ngIf="document.downloadUrl"
                class="p-4 bg-base w-full h-[75vh] rounded-lg"
                frameBorder="0"
                scrolling="auto"
                [data]="document.downloadUrl + '#toolbar=0&navpanes=0&scrollbar=0' | safe: 'resourceUrl'"
              ></object>

              <p *ngIf="!document.downloadUrl" class="p-3">
                <i>This document is not available yet.</i>
              </p>
            </div>
          </ng-container>

          <!-- Empty state for mobile -->
          <div *ngIf="documents.length === 0" class="flex flex-col items-center justify-center p-8">
            <vs-icon name="file-x-02" size="48" class="text-gray-400 mb-3"></vs-icon>
            <p class="text-sm text-gray-600 text-center">No documents available for this policy.</p>
          </div>
        </div>
      </vs-tab>

      <vs-tab [config]="{ label: warranty.asset?.propertySet?.name ?? 'Asset' | titlecase }">
        <ws-warranty-asset-card
          *ngIf="warranty.propertySetSubmission"
          [propertySetId]="warranty.propertySetSubmission.setId"
          [propertyValues]="warranty.propertySetSubmission.value"
          [dimensionValues]="warranty.propertySetSubmission.dimensionValues || {}"
        ></ws-warranty-asset-card>
      </vs-tab>

      <vs-tab [config]="{ label: 'Custom Fields' }">
        <ws-warranty-custom-fields-card [warranty]="warranty"></ws-warranty-custom-fields-card>
      </vs-tab>

      <vs-tab [config]="{ label: 'Audit' }">
        <ws-warranty-audit-page [warranty$]="warranty$"></ws-warranty-audit-page>
      </vs-tab>

      <vs-tab
        [config]="{
          label: 'Claims',
          iconEnd: 'share-04',

          target: '_self',
          href: '/claims?warrantyId=' + warranty.id,
        }"
      ></vs-tab>
    </vs-tabs>

    <ws-preview-pdf-dialog
      *ngIf="warranty"
      #previewPDFDialog
      class="h-full"
      [document]="selectedDocument"
    ></ws-preview-pdf-dialog>
  </vs-card-content>
</vs-card>

<vs-dialog #confirmationDialog (closed)="$event && unlinkDistributor()">
  <vs-dialog-header
    class="block mb-6"
    [config]="{
      mode: 'simple',
      showDivider: false,
      hideCloseButton: true,

      iconTheme: 'primary',
      icon: 'alert-triangle',

      title: 'Confirmation',
      subtitle: 'Are you sure you wish to unlink this warranty from its distributor?',
    }"
  ></vs-dialog-header>

  <div class="flex items-center gap-2">
    <button vs-button class="flex-1" appearance="outline" (click)="confirmationDialog.close(false)">Cancel</button>
    <button vs-button class="flex-1" theme="primary" (click)="confirmationDialog.close(true)">Confirm</button>
  </div>
</vs-dialog>
